import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LanguageDetectionModalProps {
  show: boolean;
  detectedCountryCode: string;
  detectedCountryName: string;
  recommendedLang: string;
  onSwitch: (remember: boolean) => void;
  onKeep: (remember: boolean) => void;
  onClose: () => void;
}

const LANGUAGES_METADATA: Record<string, { name: string; flag: string }> = {
  'en': { name: 'English', flag: '🇺🇸' },
  'es': { name: 'Español', flag: '🇪🇸' },
  'fr': { name: 'Français', flag: '🇫🇷' },
  'de': { name: 'Deutsch', flag: '🇩🇪' },
  'pt': { name: 'Português', flag: '🇵🇹' },
  'ja': { name: '日本語', flag: '🇯🇵' },
  'zh': { name: '中文', flag: '🇨🇳' },
  'ru': { name: 'Русский', flag: '🇷🇺' },
  'ar': { name: 'العربية', flag: '🇸🇦' },
  'hi': { name: 'हिन्दी', flag: '🇮🇳' },
  'sw': { name: 'Kiswahili', flag: '🇰🇪' }
};

export const LanguageDetectionModal: React.FC<LanguageDetectionModalProps> = ({
  show,
  detectedCountryCode,
  detectedCountryName,
  recommendedLang,
  onSwitch,
  onKeep,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const [rememberChoice, setRememberChoice] = useState(true);

  const currentLangCode = i18n.language?.split('-')[0] || 'en';
  const recommendedMeta = LANGUAGES_METADATA[recommendedLang] || LANGUAGES_METADATA['en'];
  const currentMeta = LANGUAGES_METADATA[currentLangCode] || LANGUAGES_METADATA['en'];

  // Localize the country name in the target language if available in i18n resources, otherwise fallback to the name from GeoIP
  const localizedCountry = t(`countries.${detectedCountryCode.toLowerCase()}`, { 
    lng: recommendedLang, 
    defaultValue: detectedCountryName 
  });

  // Localize language name in the recommended language
  const localizedLanguage = recommendedMeta.name;

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
          {/* Blur Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#050812]/85 backdrop-blur-md"
          />

          {/* Modal Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="bg-[#0d1526] w-full max-w-md rounded-[2.5rem] p-8 border border-white/10 shadow-2xl relative overflow-hidden z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Glow Strip */}
            <div className="absolute top-0 left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-[#10B981] to-transparent" />

            {/* Background Orbs */}
            <div className="absolute -top-16 -right-16 w-36 h-36 bg-[#10B981]/5 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-36 h-36 bg-blue-500/5 rounded-full blur-2xl pointer-events-none" />

            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 hover:scale-105 active:scale-95 transition-all"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            {/* Content Container */}
            <div className="relative z-10 flex flex-col items-center text-center gap-6 mt-2">
              {/* Globe Icon Wrapper */}
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#10B981]/20 to-[#06B6D4]/20 border border-[#10B981]/30 flex items-center justify-center text-[#10B981] shadow-lg shadow-[#10B981]/5">
                <Globe size={32} className="animate-pulse" style={{ animationDuration: '4s' }} />
              </div>

              {/* Title & Message (Rendered in recommended language) */}
              <div className="space-y-3">
                <h3 className="text-xl font-extrabold text-white tracking-tight leading-tight font-outfit">
                  {t('lang_detector.title', { lng: recommendedLang })}
                </h3>
                <p className="text-sm font-medium text-gray-400 leading-relaxed px-2 font-sans">
                  {t('lang_detector.message', {
                    lng: recommendedLang,
                    country: `${recommendedMeta.flag} ${localizedCountry}`,
                    language: localizedLanguage
                  })}
                </p>
              </div>

              {/* Remember Choice Checkbox */}
              <button
                onClick={() => setRememberChoice(prev => !prev)}
                className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl hover:bg-white/5 transition-colors group cursor-pointer"
              >
                <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                  rememberChoice 
                    ? 'bg-[#10B981] border-[#10B981] text-[#0d1526]' 
                    : 'border-white/20 text-transparent group-hover:border-white/30'
                }`}>
                  <Check size={12} strokeWidth={3} />
                </div>
                <span className="text-xs font-semibold text-gray-400 group-hover:text-gray-300 select-none">
                  {t('lang_detector.dont_ask', { lng: recommendedLang })}
                </span>
              </button>

              {/* Actions */}
              <div className="flex flex-col w-full gap-3 mt-2">
                {/* Switch to Recommended Language */}
                <button
                  onClick={() => onSwitch(rememberChoice)}
                  className="w-full bg-gradient-to-r from-[#10B981] to-[#06B6D4] text-[#0A0F1E] py-4 rounded-2xl font-extrabold text-xs uppercase tracking-[0.2em] shadow-lg shadow-[#10B981]/15 hover:shadow-[#10B981]/25 hover:scale-[1.01] active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer font-outfit"
                >
                  {t('lang_detector.switch_btn', {
                    lng: recommendedLang,
                    language: recommendedMeta.name,
                    flag: recommendedMeta.flag
                  })}
                </button>

                {/* Keep Current Language */}
                <button
                  onClick={() => onKeep(rememberChoice)}
                  className="w-full py-3.5 text-gray-400 hover:text-white rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-white/5 active:scale-95 transition-all cursor-pointer font-outfit"
                >
                  {t('lang_detector.keep_btn', {
                    lng: recommendedLang,
                    language: currentMeta.name
                  })}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
